import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { BRAND } from "@/config/brand";
import { MAIN_NAV } from "@/config/nav";
import { cn } from "@/utils/cn";

interface PublicHeaderProps {
  transparent?: boolean;
}

export default function PublicHeader({ transparent = false }: PublicHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const solid = !transparent || scrolled;

  return (
    <header
      className={cn(
        "fixed top-9 inset-x-0 z-40 transition-colors duration-300",
        solid ? "bg-secondary-950 backdrop-blur border-b border-background-200" : "bg-transparent"
      )}
    >
      <div className="container-page flex h-[68px] items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5 shrink-0">
          <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary-500 text-background-50 font-heading font-bold text-sm">
            {BRAND.initials}
          </span>
          <span className="font-heading font-semibold text-base transition-colors text-background-50">
            {BRAND.name}
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Navigation principale">
          {MAIN_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  "rounded-md px-3.5 py-2 text-sm font-label font-medium transition-colors cursor-pointer",
                  isActive
                    ? "text-background-50 bg-background-50/15"
                    : "text-background-50/85 hover:text-background-50 hover:bg-background-50/10"
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2 shrink-0">
          <Link
            to="/connexion"
            className="rounded-md px-4 py-2 text-sm font-label font-medium transition-colors cursor-pointer whitespace-nowrap text-background-50 hover:bg-background-50/10"
          >
            Connexion
          </Link>
          <Link
            to="/inscription"
            className="rounded-md bg-primary-500 px-4 py-2 text-sm font-label font-medium text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Créer un compte
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
          aria-expanded={menuOpen}
          className="lg:hidden flex items-center justify-center w-10 h-10 rounded-md cursor-pointer transition-colors text-background-50 hover:bg-background-50/10"
        >
          <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} aria-hidden="true" />
        </button>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 animate-fade-in">
          <nav className="container-page flex flex-col gap-1 py-4" aria-label="Navigation mobile">
            {MAIN_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    "rounded-md px-3 py-2.5 text-sm font-label font-medium",
                    isActive ? "bg-primary-50 text-primary-700" : "text-foreground-800 hover:bg-background-100"
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="mt-3 flex flex-col gap-2">
              <Link
                to="/connexion"
                className="rounded-md border border-foreground-200 px-4 py-2.5 text-center text-sm font-label font-medium text-foreground-900 hover:bg-background-100"
              >
                Connexion
              </Link>
              <Link
                to="/inscription"
                className="rounded-md bg-primary-500 px-4 py-2.5 text-center text-sm font-label font-medium text-background-50 hover:bg-primary-600"
              >
                Créer un compte
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}